import { jsPDF } from "jspdf";

const AMBER = [245, 158, 11];
const SLATE = [71, 85, 105];

export function buildBinderDoc(b) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  let y = 56;

  const line = (txt, size = 10, color = [30, 41, 59], font = "helvetica", style = "normal") => {
    doc.setFont(font, style); doc.setFontSize(size); doc.setTextColor(...color);
    doc.text(String(txt), 48, y); y += size + 6;
  };
  const rule = () => { doc.setDrawColor(...SLATE); doc.line(48, y, W - 48, y); y += 14; };
  const pageBreak = (need = 120) => { if (y > doc.internal.pageSize.getHeight() - need) { doc.addPage(); y = 56; } };

  // Header band
  doc.setFillColor(11, 15, 23); doc.rect(0, 0, W, 40, "F");
  doc.setTextColor(...AMBER); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text("SiteControl", 48, 26);
  doc.setTextColor(148, 163, 184); doc.setFontSize(9);
  doc.text("IMMUTABLE JOB BINDER", W - 48, 26, { align: "right" });

  y = 72;
  line(b.job?.title || "Job", 18, [11, 15, 23], "helvetica", "bold");
  line(`Customer: ${b.customer?.name || "-"}`, 11);
  line(`Property: ${b.property?.address || "-"}`, 11);
  line(`Status: ${(b.job?.status || "").replace(/_/g, " ")}    Trade: ${b.job?.trade || "-"}`, 11);
  if (b.job?.qbo_ref) line(`QuickBooks: ${b.job.qbo_ref}`, 11, SLATE);
  line(`Generated: ${b.generated_at}`, 9, SLATE);
  line(`Integrity SHA-256: ${b.checksum_sha256 || "-"}`, 8, SLATE);
  rule();

  // Estimates
  line("Estimates & Change Orders", 13, [11, 15, 23], "helvetica", "bold");
  (b.estimates || []).forEach((e) => {
    line(`• ${e.kind} - $${(e.total || 0).toFixed(2)} - ${e.status}`, 10);
    (e.line_items || []).forEach((li) => line(`     ${li.qty || 1} × ${li.description || "item"} @ $${li.unit_price || 0}`, 9, SLATE));
  });
  if (!(b.estimates || []).length) line("None recorded.", 10, SLATE);
  rule(); pageBreak();

  // Payments
  line("Payments", 13, [11, 15, 23], "helvetica", "bold");
  (b.payments || []).forEach((p) => line(`• ${p.kind} - $${Number(p.amount).toFixed(2)} - ${p.status}`, 10));
  if (!(b.payments || []).length) line("None recorded.", 10, SLATE);
  rule(); pageBreak();

  // Signatures (with audit metadata + image)
  line("Signature Audit", 13, [11, 15, 23], "helvetica", "bold");
  (b.signatures || []).forEach((s) => {
    line(`Signer: ${s.signer_name}`, 10);
    line(`UTC: ${s.signed_at_utc}   IP: ${s.ip || "-"}   Device: ${(s.device || "-").slice(0, 40)}`, 8, SLATE);
    if (s.gps?.lat) line(`GPS: ${s.gps.lat}, ${s.gps.lng}`, 8, SLATE);
    line(`Consent: ${s.consent_text || "-"}`, 8, SLATE);
    if (s.signature_image) {
      try { doc.addImage(s.signature_image, "PNG", 48, y, 180, 60); y += 70; } catch (e) { /* skip */ }
    }
    y += 6; pageBreak();
  });
  if (!(b.signatures || []).length) line("No signatures on file.", 10, SLATE);
  rule(); pageBreak(200);

  // Approved AI extractions (with reviewer trail)
  line("Approved AI Extractions", 13, [11, 15, 23], "helvetica", "bold");
  (b.approved_drafts || []).forEach((d) => {
    line(`• ${d.source} - approved by ${d.reviewer_name || "reviewer"} · ${String(d.reviewed_at || "").slice(0, 19).replace("T", " ")}`, 9);
    const ex = d.extracted || {};
    if (ex.work_performed) line(`     ${String(ex.work_performed).slice(0, 95)}`, 8, SLATE);
    if (ex.hours_worked != null) line(`     Hours: ${ex.hours_worked}`, 8, SLATE);
    pageBreak();
  });
  if (!(b.approved_drafts || []).length) line("None approved.", 10, SLATE);
  rule(); pageBreak(200);

  // Pre-Task Safety Sign-off (OSHA) - crew-lead acknowledgment + signature
  line("Pre-Task Safety Sign-off (OSHA)", 13, [11, 15, 23], "helvetica", "bold");
  const pl = b.job?.pretask_lock;
  if (pl) {
    line(`Crew lead: ${pl.crew_lead || "-"}`, 10);
    line(`Locked: ${pl.at || "-"}`, 8, SLATE);
    const acked = Object.keys(pl.items || {}).filter((k) => pl.items[k]);
    acked.forEach((k) => line(`   [x] ${k}`, 8, SLATE));
    if (pl.signature) {
      try { doc.addImage(pl.signature, "PNG", 48, y, 180, 60); y += 70; } catch (e) { /* skip */ }
    }
  } else {
    line("No pre-task sign-off recorded.", 10, SLATE);
  }
  rule(); pageBreak(200);

  // Photos
  line("Site Photos", 13, [11, 15, 23], "helvetica", "bold");
  let x = 48;
  (b.media || []).forEach((m) => {
    if (!m.data) return;
    try {
      const src = m.data.startsWith("data:") ? m.data : `data:${"image/jpeg"};base64,${m.data}`;
      doc.addImage(src, "JPEG", x, y, 150, 110);
      doc.setFontSize(7); doc.setTextColor(...SLATE);
      doc.text(`sha256:${(m.checksum_sha256 || "").slice(0, 16)}…`, x, y + 122);
      x += 166;
      if (x > W - 150) { x = 48; y += 140; pageBreak(160); }
    } catch (e) { /* skip non-image */ }
  });
  if (!(b.media || []).length) line("No photos captured.", 10, SLATE);

  // Footer on every page
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(148, 163, 184);
    doc.text(`SiteControl immutable record · ${(b.checksum_sha256 || "").slice(0, 12)} · page ${i}/${pages}`, W / 2, doc.internal.pageSize.getHeight() - 24, { align: "center" });
  }

  return doc;
}

export function generateBinder(b) {
  buildBinderDoc(b).save(`SiteControl_JobBinder_${(b.job?.title || "job").replace(/\s+/g, "_").slice(0, 40)}.pdf`);
}

export function binderDataUri(b) {
  return buildBinderDoc(b).output("datauristring");
}
